import React, {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {
  Sparkles,
  Circle,
  Heart,
  Trash2,
  Plus,
  Save,
  Eye,
  ArrowUp,
  Edit,
  X,
} from "lucide-react";
import {useAuth} from "../../hooks/useAuth";
import {useCharactersByPlayerRealtime} from "../../hooks/useFirestore";
import {useCharacterInventory} from "../../hooks/useInventory";
import {useCharacterActivity} from "../../hooks/useCharacterActivity";
import {
  createCharacter,
  updateCharacter,
  levelUpCharacter,
  spendDowntimeDays,
  deleteCharacter,
  transferDMTokenToCharacter,
} from "../../services/api/charactersService";
import {useNotifications} from "../../components/NotificationSystem";
import "../../styles/env.css";
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog";
import CharacterSheetUpload from "../../components/Character Sheet/CharacterSheetUpload";
import FilterableList from "../../components/FilterableList";
import {BaseFilters} from "../../components/ListFilters";

// Character type definition
interface Character {
  id: string;
  name: string;
  species: string;
  class: string;
  level: number;
  mementoTokens: number;
  downtimeDays: number;
  status: string;
  inventory: {
    name: string;
    type: string;
    amount: number;
    description: string;
  }[];
  currency?: {
    platinum: number;
    gold: number;
    electrum: number;
    silver: number;
    copper: number;
  };
  sheetUrl?: string;
  sheetPath?: string;
  fileName?: string;
  will?: {beneficiary: string; description: string};
}

// Character filters interface extending base filters
interface CharacterFilters extends BaseFilters {
  status: "all" | "active" | "retired";
  class: string;
  minLevel: number;
  maxLevel: number;
}

const initialCharacter: Omit<
  Character,
  "id" | "mementoTokens" | "status" | "inventory"
> = {
  name: "",
  species: "",
  class: "",
  level: 1,
  downtimeDays: 0,
  sheetUrl: "",
};

const Characters: React.FC = () => {
  const {
    user,
    isLoggedIn,
    loading: authLoading,
    roles,
    updateLocalUserState,
  } = useAuth();
  const navigate = useNavigate();
  const {id: routeCharacterId} = useParams<{id?: string}>();
  // Use targeted real-time listener for current user's characters only
  const {
    data: characters,
    loading: charactersLoading,
    error,
  } = useCharactersByPlayerRealtime(user?.uid || null);
  const {addNotification} = useNotifications();

  // Inventory state
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(
    null
  );

  const [state, setState] = useState<"empty" | "creating" | "list" | "view">(
    "list"
  );
  const [form, setForm] = useState(initialCharacter);
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  // Inventory form state
  const [itemForm, setItemForm] = useState({
    name: "",
    amount: "1",
    description: "",
    attunement: false,
  });
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemForm, setEditingItemForm] = useState<{
    name: string;
    quantity: string;
    description: string;
  }>({name: "", quantity: "1", description: ""});
  const [isSavingItemEdit, setIsSavingItemEdit] = useState(false);
  // Editable character form state for view
  const [editForm, setEditForm] = useState<{
    name: string;
    class: string;
    species: string;
    inventory: Character["inventory"];
    will: {beneficiary: string; description: string};
  }>({
    name: "",
    class: "",
    species: "",
    inventory: [],
    will: {beneficiary: "", description: ""},
  });

  // Currency state
  const [currencyForm, setCurrencyForm] = useState<{
    platinum: string;
    gold: string;
    electrum: string;
    silver: string;
    copper: string;
  }>({
    platinum: "",
    gold: "",
    electrum: "",
    silver: "",
    copper: "",
  });
  const [isSavingCurrency, setIsSavingCurrency] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteItemIdx, setPendingDeleteItemIdx] = useState<
    number | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [levelUpDialogOpen, setLevelUpDialogOpen] = useState(false);
  const [isLevelingUp, setIsLevelingUp] = useState(false);
  const [downtimeDialogOpen, setDowntimeDialogOpen] = useState(false);
  const [isSpendingDowntime, setIsSpendingDowntime] = useState(false);
  const [downtimeDaysToSpend, setDowntimeDaysToSpend] = useState<number>(1);
  const [downtimeDescription, setDowntimeDescription] = useState<string>("");
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState<{
    name: string;
    description: string;
  } | null>(null);
  const [dmTokenDialogOpen, setDmTokenDialogOpen] = useState(false);
  const [isTransferringTokens, setIsTransferringTokens] = useState(false);
  const [tokensToTransfer, setTokensToTransfer] = useState<number>(1);

  // Character deletion state
  const [deleteCharacterDialogOpen, setDeleteCharacterDialogOpen] =
    useState(false);
  const [deleteCharacterName, setDeleteCharacterName] = useState("");
  const [isDeletingCharacter, setIsDeletingCharacter] = useState(false);

  // Notify on load errors (top-level effect to avoid conditional hooks and rerender loops)
  React.useEffect(() => {
    if (error) {
      addNotification({
        type: "error",
        title: "Loading Error",
        message: `Failed to load characters: ${error}`,
        persistent: true,
      });
    }
  }, [error, addNotification]);

  // Character filters state
  const [filters, setFilters] = useState<CharacterFilters>({
    search: "",
    status: "all",
    class: "",
    minLevel: 1,
    maxLevel: 20,
  });

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      class: "",
      minLevel: 1,
      maxLevel: 20,
    });
  };

  // Inventory hook for selected character
  const inventory = useCharacterInventory(selectedCharacterId || "");

  // Activity hook for selected character
  const {activity: characterActivity} = useCharacterActivity(
    selectedCharacterId || null
  );

  const startEditInventoryItem = (item: any) => {
    setEditingItemId(item.id || null);
    setEditingItemForm({
      name: item.name || "",
      quantity: String(item.quantity ?? 1),
      description: item.description || "",
    });
  };

  const cancelEditInventoryItem = () => {
    setEditingItemId(null);
    setEditingItemForm({name: "", quantity: "1", description: ""});
  };

  const saveEditInventoryItem = async (itemId: string) => {
    if (!editingItemId || editingItemId !== itemId) return;
    const newName = editingItemForm.name.trim();
    const newQty = parseInt(editingItemForm.quantity, 10) || 0;
    if (!newName || newQty < 1) return;
    setIsSavingItemEdit(true);
    try {
      await inventory.updateItem(itemId, {
        name: newName,
        quantity: newQty,
        description: editingItemForm.description,
      });
      addNotification({
        type: "success",
        title: "Inventory Updated",
        message: "Item changes saved.",
      });
      cancelEditInventoryItem();
    } catch (err: any) {
      addNotification({
        type: "error",
        title: "Update Failed",
        message: err?.message || "Could not save item changes.",
      });
    } finally {
      setIsSavingItemEdit(false);
    }
  };

  // Update state based on characters data (avoid fighting with "creating" or explicit view state)
  React.useEffect(() => {
    if (charactersLoading) return;
    // Do not auto-switch when the user is creating or viewing a character
    if (state === "creating" || state === "view") return;
    // Auto-select empty vs list
    const desired = characters.length === 0 ? "empty" : "list";
    if (state !== desired) {
      setState(desired);
    }
  }, [charactersLoading, characters.length, state]);

  // Sync URL param with view state when characters load
  React.useEffect(() => {
    if (charactersLoading) return;
    // If there is an id in the URL, ensure we are in view state for that character
    if (routeCharacterId) {
      const idx = characters.findIndex((c) => c.id === routeCharacterId);
      if (idx !== -1) {
        if (viewIndex !== idx || state !== "view") {
          enterViewForCharacter(characters[idx] as any, idx);
        }
      }
      return;
    }
    // If there is no id in the URL:
    // - Do not override "creating" or "empty" states
    // - If we were previously viewing a character, exit view to the correct state
    if (state === "view") {
      setViewIndex(null);
      setSelectedCharacterId(null);
      setState(characters.length === 0 ? "empty" : "list");
    }
    // For other states ("list", "empty", "creating") no action here;
    // list/empty transitions are handled by the separate effect above.
  }, [
    charactersLoading,
    routeCharacterId,
    characters.length,
    characters,
    state,
    viewIndex,
  ]);

  // Handle form input for character creation
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {name, value, type} = e.target;
    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle inventory form input
  const handleItemChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const {name, value} = e.target;
    setItemForm((prev) => ({...prev, [name]: value}));
  };

  // Add item to Firebase inventory
  const handleAddFirebaseItem = async () => {
    if (!selectedCharacterId || !itemForm.name || !itemForm.amount) {
      addNotification({
        type: "error",
        title: "Missing Information",
        message: "Please fill in all required fields (name, amount).",
      });
      return;
    }

    try {
      await inventory.addItem(selectedCharacterId, {
        name: itemForm.name,
        type: "item" as any,
        rarity: "common" as any,
        weight: 0,
        value: 0,
        description: itemForm.description,
        quantity: parseInt(itemForm.amount),
        magicalProperties: [],
        attunement: itemForm.attunement,
        equipped: false,
        source: "session",
      });

      setItemForm({name: "", amount: "", description: "", attunement: false});
      addNotification({
        type: "success",
        title: "Item Added",
        message: `Added ${itemForm.name} to inventory.`,
      });
    } catch (error) {
      addNotification({
        type: "error",
        title: "Add Failed",
        message:
          error instanceof Error
            ? error.message
            : "Failed to add item to inventory.",
      });
    }
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteItemIdx !== null) {
      setEditForm((prev) => ({
        ...prev,
        inventory: prev.inventory.filter((_, i) => i !== pendingDeleteItemIdx),
      }));
      setPendingDeleteItemIdx(null);
    }
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteItemIdx(null);
  };

  // Handle description modal
  const handleShowDescription = (itemName: string, description: string) => {
    setSelectedDescription({name: itemName, description});
    setDescriptionModalOpen(true);
  };

  const handleCloseDescription = () => {
    setDescriptionModalOpen(false);
    setSelectedDescription(null);
  };

  // Character deletion handlers
  const handleDeleteCharacter = () => {
    setDeleteCharacterDialogOpen(true);
    setDeleteCharacterName("");
  };

  const handleConfirmDeleteCharacter = async () => {
    if (
      viewIndex === null ||
      viewIndex === undefined ||
      !characters[viewIndex]
    ) {
      return;
    }

    const character = characters[viewIndex];

    if (deleteCharacterName.trim() !== character.name.trim()) {
      addNotification({
        type: "error",
        title: "Invalid Character Name",
        message:
          "Character name does not match. Please type the exact character name to confirm deletion.",
      });
      return;
    }

    setIsDeletingCharacter(true);
    try {
      await deleteCharacter({
        characterId: character.id || "",
        characterName: character.name || "",
      });

      addNotification({
        type: "success",
        title: "Character Deleted",
        message: `Character "${character.name || "Unknown"}" has been deleted successfully.`,
      });
      setDeleteCharacterDialogOpen(false);
      setDeleteCharacterName("");
      setViewIndex(null);
      setState("list");
    } catch (error: any) {
      console.error("Error deleting character:", error);
      addNotification({
        type: "error",
        title: "Delete Failed",
        message:
          error.message || "Failed to delete character. Please try again.",
      });
    } finally {
      setIsDeletingCharacter(false);
    }
  };

  const handleCancelDeleteCharacter = () => {
    setDeleteCharacterDialogOpen(false);
    setDeleteCharacterName("");
  };

  // Handle edit form input for name, class, species, and will
  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target;
    if (name === "will.beneficiary" || name === "will.description") {
      const willField = name.split(".")[1] as "beneficiary" | "description";
      setEditForm((prev) => ({
        ...prev,
        will: {
          beneficiary: prev.will?.beneficiary || "",
          description: prev.will?.description || "",
          [willField]: value,
        },
      }));
    } else {
      setEditForm((prev) => ({...prev, [name]: value}));
    }
  };

  // Handle currency form input
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setCurrencyForm((prev) => ({...prev, [name]: value}));
  };

  // Save currency to character
  const handleSaveCurrency = async () => {
    if (!characters || viewIndex === null || !characters[viewIndex].id) {
      addNotification({
        type: "error",
        title: "Cannot Save",
        message: "Character not found. Please try again.",
      });
      return;
    }

    setIsSavingCurrency(true);
    try {
      await updateCharacter({
        characterId: characters[viewIndex].id,
        updates: {
          currency: {
            platinum: parseInt(currencyForm.platinum) || 0,
            gold: parseInt(currencyForm.gold) || 0,
            electrum: parseInt(currencyForm.electrum) || 0,
            silver: parseInt(currencyForm.silver) || 0,
            copper: parseInt(currencyForm.copper) || 0,
          },
        },
      });

      addNotification({
        type: "success",
        title: "Currency Saved",
        message: "Character currency updated successfully!",
      });
    } catch (error) {
      addNotification({
        type: "error",
        title: "Save Failed",
        message:
          error instanceof Error ? error.message : "Failed to save currency.",
      });
    } finally {
      setIsSavingCurrency(false);
    }
  };

  // Helper function to ensure numeric values
  const ensureNumber = (value: any): number => {
    return typeof value === "string" ? parseInt(value, 10) : value;
  };

  // Level-up helper functions
  const getTokensRequiredForNextLevel = (currentLevel: number): number => {
    const nextLevel = currentLevel + 1;
    if (nextLevel <= 4) return 1; // Tier 1: levels 1-4
    if (nextLevel <= 8) return 2; // Tier 2: levels 5-8
    if (nextLevel <= 12) return 3; // Tier 3: levels 9-12
    if (nextLevel <= 16) return 4; // Tier 4: levels 13-16
    if (nextLevel <= 20) return 5; // Tier 5: levels 17-20
    return 0; // Max level reached
  };

  const canLevelUp = (character: any): boolean => {
    const level = ensureNumber(character.level);
    const mementoTokens = ensureNumber(character.mementoTokens);

    if (level >= 20) return false; // Max level
    const tokensRequired = getTokensRequiredForNextLevel(level);
    return mementoTokens >= tokensRequired;
  };

  const handleLevelUp = () => {
    setLevelUpDialogOpen(true);
  };

  const handleConfirmLevelUp = async () => {
    if (!characters || viewIndex === null) return;

    const character = characters[viewIndex];

    if (!character.id) {
      addNotification({
        type: "error",
        title: "Cannot Level Up",
        message: "Character ID is missing. Please try again.",
      });
      return;
    }

    if (!canLevelUp(character)) {
      addNotification({
        type: "error",
        title: "Cannot Level Up",
        message: "Not enough memento tokens to level up.",
      });
      return;
    }

    // Prevent multiple simultaneous level up attempts
    if (isLevelingUp) {
      // Level up already in progress, ignoring duplicate request
      return;
    }

    // Prepare level up attempt data
    const currentLevel = ensureNumber(character.level);
    const tokensRequired = getTokensRequiredForNextLevel(currentLevel);

    setIsLevelingUp(true);
    try {
      const result = await levelUpCharacter({
        characterId: character.id,
      });

      // Validate the result matches expectations
      const expectedLevel = currentLevel + 1;

      // Validate level up result matches expectations
      if (result.newLevel !== expectedLevel) {
        addNotification({
          type: "error",
          title: "Level Up Warning",
          message: `Expected level ${expectedLevel}, but character is now level ${result.newLevel}`,
        });
      }

      if (result.tokensSpent !== tokensRequired) {
        addNotification({
          type: "error",
          title: "Token Warning",
          message: `Expected to spend ${tokensRequired} tokens, but ${result.tokensSpent} were spent`,
        });
      }

      addNotification({
        type: "success",
        title: "Level Up!",
        message: `${character.name} has leveled up to level ${result.newLevel}! (${result.tokensSpent} tokens spent)`,
      });

      setLevelUpDialogOpen(false);
    } catch (error: any) {
      // Error handling for character level up operations
      addNotification({
        type: "error",
        title: "Level Up Failed",
        message: error.message || "Failed to level up character",
      });
    } finally {
      setIsLevelingUp(false);
    }
  };

  const handleCancelLevelUp = () => {
    setLevelUpDialogOpen(false);
  };

  const handleSpendDowntime = () => {
    setDowntimeDialogOpen(true);
    setDowntimeDaysToSpend(1); // Reset to 1 when opening modal
    setDowntimeDescription("");
  };

  const handleConfirmSpendDowntime = async () => {
    if (!characters || viewIndex === null) return;

    const character = characters[viewIndex];

    if (!character.id) {
      addNotification({
        type: "error",
        title: "Cannot Spend Downtime",
        message: "Character ID is missing. Please try again.",
      });
      return;
    }

    if (downtimeDaysToSpend <= 0) {
      addNotification({
        type: "error",
        title: "Invalid Amount",
        message: "Please enter a valid number of downtime days to spend.",
      });
      return;
    }

    if (downtimeDaysToSpend > ensureNumber(character.downtimeDays)) {
      addNotification({
        type: "error",
        title: "Insufficient Downtime",
        message: "You don't have enough downtime days to spend.",
      });
      return;
    }

    // Prevent multiple simultaneous downtime spending attempts
    if (isSpendingDowntime) {
      return;
    }

    setIsSpendingDowntime(true);
    try {
      const result = await spendDowntimeDays({
        characterId: character.id,
        daysToSpend: downtimeDaysToSpend,
        activityDescription: downtimeDescription.trim() || undefined,
      });

      addNotification({
        type: "success",
        title: "Downtime Spent",
        message: `${character.name} has spent ${result.daysSpent} downtime days. This action cannot be undone.`,
      });

      setDowntimeDialogOpen(false);
    } catch (error: any) {
      addNotification({
        type: "error",
        title: "Failed to Spend Downtime",
        message: error.message || "Failed to spend downtime days",
      });
    } finally {
      setIsSpendingDowntime(false);
    }
  };

  const handleCancelSpendDowntime = () => {
    setDowntimeDialogOpen(false);
    setDowntimeDaysToSpend(1);
    setDowntimeDescription("");
  };

  // DM Token Transfer handlers
  const canTransferDMTokens = (): boolean => {
    return (
      roles.includes("dm") ||
      roles.includes("moderator") ||
      roles.includes("admin")
    );
  };

  const getUserDMTokens = (): number => {
    return user?.dmTokens || 0;
  };

  const handleTransferDMTokens = () => {
    setDmTokenDialogOpen(true);
    setTokensToTransfer(1); // Reset to 1 when opening modal
  };

  const handleConfirmTransferDMTokens = async () => {
    if (!characters || viewIndex === null) return;

    const character = characters[viewIndex];

    if (!character.id) {
      addNotification({
        type: "error",
        title: "Cannot Transfer Tokens",
        message: "Character ID is missing. Please try again.",
      });
      return;
    }

    if (tokensToTransfer <= 0) {
      addNotification({
        type: "error",
        title: "Invalid Amount",
        message: "Please enter a valid number of tokens to transfer.",
      });
      return;
    }

    const availableDMTokens = getUserDMTokens();
    if (tokensToTransfer > availableDMTokens) {
      addNotification({
        type: "error",
        title: "Insufficient DM Tokens",
        message: `You don't have enough DM tokens. Available: ${availableDMTokens}, Requested: ${tokensToTransfer}`,
      });
      return;
    }

    // Prevent multiple simultaneous transfer attempts
    if (isTransferringTokens) {
      return;
    }

    setIsTransferringTokens(true);
    try {
      const result = await transferDMTokenToCharacter({
        characterId: character.id,
        tokensToTransfer: tokensToTransfer,
      });

      addNotification({
        type: "success",
        title: "DM Tokens Transferred",
        message: `Successfully transferred ${tokensToTransfer} DM token(s) to ${character.name}. You now have ${result.newDMTokens} DM tokens remaining.`,
      });

      // Update local user state with new DM token count
      if (user) {
        updateLocalUserState({dmTokens: result.newDMTokens});
      }

      setDmTokenDialogOpen(false);
    } catch (error: any) {
      addNotification({
        type: "error",
        title: "Failed to Transfer Tokens",
        message: error.message || "Failed to transfer DM tokens",
      });
    } finally {
      setIsTransferringTokens(false);
    }
  };

  const handleCancelTransferDMTokens = () => {
    setDmTokenDialogOpen(false);
    setTokensToTransfer(1);
  };

  // Helper to enter view state for a given character without navigation
  const enterViewForCharacter = (character: Character, idx: number) => {
    setViewIndex(idx);
    setEditForm({
      name: character.name,
      class: character.class,
      species: character.species,
      inventory: [...character.inventory],
      will: character.will || {beneficiary: "", description: ""},
    });
    setCurrencyForm({
      platinum: character.currency?.platinum?.toString() || "",
      gold: character.currency?.gold?.toString() || "",
      electrum: character.currency?.electrum?.toString() || "",
      silver: character.currency?.silver?.toString() || "",
      copper: character.currency?.copper?.toString() || "",
    });
    setSelectedCharacterId(character.id || null);
    setState("view");
  };

  // When entering view mode from list, navigate to URL-based detail view
  const handleView = (idx: number) => {
    const character = characters[idx];
    // Navigate first to ensure URL is the source of truth; the URL effect will set view state
    if (character.id) {
      navigate(`/characters/${character.id}`);
    }
  };

  // Save all edits to character
  const handleSave = async () => {
    if (
      viewIndex === null ||
      !characters[viewIndex] ||
      !characters[viewIndex].id
    ) {
      addNotification({
        type: "error",
        title: "Save Failed",
        message: "Unable to save character changes. Please try again.",
        persistent: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const characterToUpdate = characters[viewIndex];
      const characterId = characterToUpdate.id;
      if (!characterId) {
        addNotification({
          type: "error",
          title: "Save Failed",
          message: "Character ID is missing. Please try again.",
          persistent: true,
        });
        return;
      }

      await updateCharacter({
        characterId,
        updates: {
          name: editForm.name,
          class: editForm.class,
          species: editForm.species,
          inventory: editForm.inventory,
          will: editForm.will,
        },
      });
      setState("list");
      setViewIndex(null);
      addNotification({
        type: "success",
        title: "Character Updated",
        message: `Character "${editForm.name}" has been updated successfully!`,
        duration: 3000,
      });
    } catch (error) {
      // Error handling for character update operations
      addNotification({
        type: "error",
        title: "Update Failed",
        message:
          error instanceof Error
            ? error.message
            : "Failed to update character. Please try again.",
        persistent: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel actions
  const handleCancel = () => {
    setForm(initialCharacter);
    setState(characters.length === 0 ? "empty" : "list");
    setViewIndex(null);
    setItemForm({name: "", amount: "1", description: "", attunement: false});
    // If we were on a URL with a character id, navigate back to the list URL
    if (routeCharacterId) {
      navigate(`/characters`);
    }
  };

  // Create new character
  const handleCreateCharacter = async () => {
    if (!isLoggedIn || !user?.uid) {
      addNotification({
        type: "error",
        title: "Authentication Error",
        message: "You must be logged in to create a character.",
        persistent: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createCharacter({
        name: form.name,
        species: form.species,
        class: form.class,
        level: form.level,
        inventory: [],
      });
      setForm(initialCharacter);
      setState("list");
      addNotification({
        type: "success",
        title: "Character Created",
        message: `Character "${form.name}" has been created successfully!`,
        duration: 3000,
      });
    } catch (error) {
      // Error handling for character creation operations
      addNotification({
        type: "error",
        title: "Creation Failed",
        message:
          error instanceof Error
            ? error.message
            : "Failed to create character. Please try again.",
        persistent: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (authLoading || charactersLoading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Characters</h2>
        <div className="env-loading">Loading characters...</div>
      </div>
    );
  }

  // Show authentication error
  if (!isLoggedIn) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Characters</h2>
        <div className="env-error">Please log in to view your characters.</div>
      </div>
    );
  }

  // Show error state UI
  if (error) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Characters</h2>
        <div className="env-error">
          Unable to load characters. Please refresh the page.
        </div>
      </div>
    );
  }

  // Render states
  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Characters</h2>
      <section className="env-section">
        <div className="env-section-header">
          {state !== "creating" && (
            <button
              className="btn btn-tertiary"
              onClick={() => setState("creating")}
            >
              + Create new character
            </button>
          )}
        </div>
        {state === "empty" && (
          <div className="env-empty-state">No characters created</div>
        )}
        {state === "creating" && (
          <div className="env-card-medium">
            <div className="env-character-creation-title">
              CREATING YOUR CHARACTER
            </div>
            <div className="env-form-group">
              <label htmlFor="create-character-name">Name</label>
              <input
                id="create-character-name"
                name="name"
                placeholder="Character name"
                aria-label="Character name"
                value={form.name}
                onChange={handleChange}
                className="env-input"
              />
              <label htmlFor="create-character-species">Species</label>
              <input
                id="create-character-species"
                name="species"
                placeholder="Species"
                aria-label="Species"
                value={form.species}
                onChange={handleChange}
                className="env-input"
              />
              <label htmlFor="create-character-class">Class</label>
              <input
                id="create-character-class"
                name="class"
                placeholder="Class"
                aria-label="Class"
                value={form.class}
                onChange={handleChange}
                className="env-input"
              />
              <label htmlFor="create-character-level">Level</label>
              <input
                id="create-character-level"
                name="level"
                type="number"
                placeholder="Level"
                aria-label="Level"
                value={form.level}
                onChange={handleChange}
                className="env-input"
              />
            </div>
            <div className="env-character-form-actions">
              <button
                className="btn env-btn-full env-character-form-btn"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary env-btn-full env-character-form-btn"
                onClick={handleCreateCharacter}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create character"}
              </button>
            </div>
          </div>
        )}
        {state === "list" && (
          <div className="env-character-list-container">
            <FilterableList
              data={characters}
              loading={charactersLoading}
              error={error}
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={clearFilters}
              searchFields={["name", "class", "species"]}
              searchPlaceholder="Search characters by name, class, or species..."
              emptyMessage="No characters found matching your criteria"
              additionalFilter={(character, filters) => {
                // Status filter
                if (
                  filters.status === "active" &&
                  character.status !== "active"
                )
                  return false;
                if (
                  filters.status === "retired" &&
                  character.status !== "retired"
                )
                  return false;

                // Class filter
                if (filters.class && character.class !== filters.class)
                  return false;

                // Level range filter
                const level = ensureNumber(character.level);
                if (level < filters.minLevel || level > filters.maxLevel)
                  return false;

                return true;
              }}
              renderItem={(character, index) => (
                <div className="env-character-list-item">
                  <div className="env-character-info">
                    <h3>
                      {character.name || "Character name"}{" "}
                      <span className="env-character-class">
                        {character.class || "Class"}
                      </span>
                    </h3>
                    <p className="env-character-details">
                      Level {ensureNumber(character.level) || 1} •{" "}
                      {character.species || "Species"} •{" "}
                      {character.status || "unknown"}
                    </p>
                    <p className="env-character-tokens">
                      {ensureNumber(character.mementoTokens) || 0} memento
                      tokens • {character.downtimeDays || 0} downtime days
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleView(index)}
                  >
                    View character
                  </button>
                </div>
              )}
              customActiveFilters={
                <>
                  {filters.status !== "all" && (
                    <Typography
                      variant="body2"
                      sx={{
                        backgroundColor: "var(--sub)",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      Status: {filters.status}
                    </Typography>
                  )}
                  {filters.class && (
                    <Typography
                      variant="body2"
                      sx={{
                        backgroundColor: "var(--sub)",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      Class: {filters.class}
                    </Typography>
                  )}
                  {(filters.minLevel !== 1 || filters.maxLevel !== 20) && (
                    <Typography
                      variant="body2"
                      sx={{
                        backgroundColor: "var(--sub)",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      Level: {filters.minLevel}-{filters.maxLevel}
                    </Typography>
                  )}
                </>
              }
            >
              {/* Advanced filters */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {/* Status Filter */}
                <FormControl sx={{minWidth: 150}}>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={filters.status}
                    label="Status"
                    onChange={(e) =>
                      setFilters({...filters, status: e.target.value as any})
                    }
                  >
                    <MenuItem value="all">All Characters</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                  </Select>
                </FormControl>

                {/* Class Filter */}
                <FormControl sx={{minWidth: 150}}>
                  <InputLabel id="class-filter-label">Class</InputLabel>
                  <Select
                    labelId="class-filter-label"
                    value={filters.class}
                    label="Class"
                    onChange={(e) =>
                      setFilters({...filters, class: e.target.value})
                    }
                  >
                    <MenuItem value="">
                      <em>All Classes</em>
                    </MenuItem>
                    {/* Get unique classes from characters */}
                    {Array.from(
                      new Set(characters.map((c) => c.class).filter(Boolean))
                    ).map((characterClass) => (
                      <MenuItem key={characterClass} value={characterClass}>
                        {characterClass}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Level Range Filters */}
                <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                  <Typography variant="body2" color="textSecondary">
                    Level Range:
                  </Typography>
                  <FormControl sx={{minWidth: 80}}>
                    <InputLabel id="min-level-label">Min</InputLabel>
                    <Select
                      labelId="min-level-label"
                      value={filters.minLevel}
                      label="Min"
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          minLevel: Number(e.target.value),
                        })
                      }
                      size="small"
                    >
                      {Array.from({length: 20}, (_, i) => i + 1).map(
                        (level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="textSecondary">
                    to
                  </Typography>
                  <FormControl sx={{minWidth: 80}}>
                    <InputLabel id="max-level-label">Max</InputLabel>
                    <Select
                      labelId="max-level-label"
                      value={filters.maxLevel}
                      label="Max"
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          maxLevel: Number(e.target.value),
                        })
                      }
                      size="small"
                    >
                      {Array.from({length: 20}, (_, i) => i + 1).map(
                        (level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </FilterableList>
          </div>
        )}
        {state === "view" && viewIndex !== null && characters[viewIndex] && (
          <div className="env-card-large">
            {/* Character Header with Back Button */}
            <div className="env-character-header">
              <button className="btn btn-secondary" onClick={handleCancel}>
                Back
              </button>
            </div>
            {/* Information Section */}
            <div className="env-character-stats">
              {/* Input Fields Row - Name, Class, Species together */}
              <div className="env-character-stats-grid">
                <div className="env-form-group">
                  <label htmlFor="character-name">Name</label>
                  <input
                    id="character-name"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditFormChange}
                    placeholder="Character name"
                    className="env-inventory-form-input"
                  />
                </div>
                <div className="env-form-group">
                  <label htmlFor="character-class">Class</label>
                  <input
                    id="character-class"
                    name="class"
                    value={editForm.class}
                    onChange={handleEditFormChange}
                    placeholder="Class"
                    className="env-input"
                  />
                </div>
                <div className="env-form-group">
                  <label htmlFor="character-species">Species</label>
                  <input
                    id="character-species"
                    name="species"
                    value={editForm.species}
                    onChange={handleEditFormChange}
                    placeholder="Species"
                    className="env-input"
                  />
                </div>
              </div>

              {/* Stats Row - Level, Memento tokens, Downtime days, Status */}
              <div className="env-character-stats-grid">
                <div className="env-stat-item">
                  <div className="env-stat-label">Level</div>
                  <div className="env-stat-value env-stat-value-with-actions">
                    <span>{ensureNumber(characters[viewIndex].level)}</span>
                    {canLevelUp(characters[viewIndex]) && (
                      <button
                        className="btn btn-primary"
                        onClick={handleLevelUp}
                        disabled={isLevelingUp}
                        title="Level Up"
                      >
                        <ArrowUp size={16} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="env-stat-item">
                  <div className="env-stat-label">Memento tokens</div>
                  <div className="env-stat-value env-stat-value-with-actions">
                    <span>
                      {ensureNumber(characters[viewIndex].mementoTokens)}
                    </span>
                    {canTransferDMTokens() && getUserDMTokens() > 0 && (
                      <button
                        className="btn btn-secondary env-stat-action-btn"
                        onClick={handleTransferDMTokens}
                        title={`Transfer DM tokens (You have ${getUserDMTokens()} available)`}
                      >
                        <Plus size={12} className="env-icon-margin-right" />
                        DM
                      </button>
                    )}
                  </div>
                </div>
                <div className="env-stat-item">
                  <div className="env-stat-label">Downtime days</div>
                  <div className="env-stat-value env-stat-value-with-actions">
                    <span>
                      {ensureNumber(characters[viewIndex].downtimeDays)}
                    </span>
                    {ensureNumber(characters[viewIndex].downtimeDays) > 0 && (
                      <button
                        className="btn btn-secondary env-stat-action-btn env-stat-action-btn-downtime"
                        onClick={handleSpendDowntime}
                        disabled={isSpendingDowntime}
                        title="Spend Downtime Days"
                      >
                        <Sparkles size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="env-stat-item">
                  <div className="env-stat-label">Status</div>
                  <div className="env-stat-value">
                    {characters[viewIndex].status}
                  </div>
                </div>
              </div>
            </div>

            {/* Inventory Section */}
            <div className="env-inventory-section">
              <div className="env-inventory-header">INVENTORY</div>
              {selectedCharacterId ? (
                <div className="env-inventory-content">
                  {inventory.loading ? (
                    <div className="env-loading">Loading inventory...</div>
                  ) : inventory.error ? (
                    <div className="env-error">
                      Error loading inventory: {inventory.error}
                    </div>
                  ) : (
                    <>
                      {/* Inventory Statistics and Currency */}
                      {inventory.statistics && (
                        <div className="env-inventory-stats-currency-container">
                          <div className="env-inventory-stats">
                            <div className="env-stat-item">
                              <div className="env-stat-label">Total Items</div>
                              <div className="env-stat-value">
                                {inventory.statistics.totalItems}
                              </div>
                            </div>
                            <div className="env-stat-item">
                              <div className="env-stat-label">Attuned</div>
                              <div className="env-stat-value">
                                {inventory.statistics.attunedItems}/
                                {inventory.statistics.maxAttunement}
                              </div>
                            </div>
                          </div>
                          {/* Currency Section - 3x2 Grid */}
                          <div className="env-currency-section">
                            <div className="env-currency-grid">
                              <div className="env-inventory-form-field">
                                <label
                                  className="env-inventory-form-label"
                                  htmlFor="currency-platinum"
                                >
                                  Platinum
                                </label>
                                <input
                                  id="currency-platinum"
                                  type="number"
                                  min="0"
                                  name="platinum"
                                  aria-label="Platinum"
                                  value={currencyForm.platinum}
                                  onChange={handleCurrencyChange}
                                  className="env-inventory-form-input"
                                  placeholder="0"
                                />
                              </div>
                              <div className="env-inventory-form-field">
                                <label
                                  className="env-inventory-form-label"
                                  htmlFor="currency-gold"
                                >
                                  Gold
                                </label>
                                <input
                                  id="currency-gold"
                                  type="number"
                                  min="0"
                                  name="gold"
                                  aria-label="Gold"
                                  value={currencyForm.gold}
                                  onChange={handleCurrencyChange}
                                  className="env-inventory-form-input"
                                  placeholder="0"
                                />
                              </div>
                              <div className="env-inventory-form-field">
                                <label
                                  className="env-inventory-form-label"
                                  htmlFor="currency-electrum"
                                >
                                  Electrum
                                </label>
                                <input
                                  id="currency-electrum"
                                  type="number"
                                  min="0"
                                  name="electrum"
                                  aria-label="Electrum"
                                  value={currencyForm.electrum}
                                  onChange={handleCurrencyChange}
                                  className="env-inventory-form-input"
                                  placeholder="0"
                                />
                              </div>
                              <div className="env-inventory-form-field">
                                <label
                                  className="env-inventory-form-label"
                                  htmlFor="currency-silver"
                                >
                                  Silver
                                </label>
                                <input
                                  id="currency-silver"
                                  type="number"
                                  min="0"
                                  name="silver"
                                  aria-label="Silver"
                                  value={currencyForm.silver}
                                  onChange={handleCurrencyChange}
                                  className="env-inventory-form-input"
                                  placeholder="0"
                                />
                              </div>
                              <div className="env-inventory-form-field">
                                <label
                                  className="env-inventory-form-label"
                                  htmlFor="currency-copper"
                                >
                                  Copper
                                </label>
                                <input
                                  id="currency-copper"
                                  type="number"
                                  min="0"
                                  name="copper"
                                  aria-label="Copper"
                                  value={currencyForm.copper}
                                  onChange={handleCurrencyChange}
                                  className="env-inventory-form-input"
                                  placeholder="0"
                                />
                              </div>
                              <div className="env-inventory-form-field">
                                <div className="env-inventory-form-label">
                                  &nbsp;
                                </div>
                                <button
                                  className="btn btn-primary env-inventory-form-input env-currency-save-btn"
                                  onClick={handleSaveCurrency}
                                  disabled={isSavingCurrency}
                                >
                                  <Save size={16} />
                                  {isSavingCurrency ? "Saving..." : "Save"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Inventory Table */}
                      <table className="env-inventory-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Quantity</th>
                            <th>Description</th>
                            <th>Attunement</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventory.items.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="env-empty-inventory">
                                No items in inventory
                              </td>
                            </tr>
                          ) : (
                            inventory.items.map((item) => (
                              <tr key={item.id}>
                                <td>
                                  {editingItemId === item.id ? (
                                    <input
                                      name="editName"
                                      aria-label="Edit item name"
                                      className="env-inventory-form-input"
                                      value={editingItemForm.name}
                                      onChange={(e) =>
                                        setEditingItemForm((prev) => ({
                                          ...prev,
                                          name: e.target.value,
                                        }))
                                      }
                                      placeholder="Item name"
                                    />
                                  ) : (
                                    item.name
                                  )}
                                </td>
                                <td>
                                  {editingItemId === item.id ? (
                                    <input
                                      type="number"
                                      min={1}
                                      name="editQuantity"
                                      aria-label="Edit item quantity"
                                      className="env-inventory-form-input"
                                      value={editingItemForm.quantity}
                                      onChange={(e) =>
                                        setEditingItemForm((prev) => ({
                                          ...prev,
                                          quantity: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    item.quantity
                                  )}
                                </td>
                                <td>
                                  {editingItemId === item.id ? (
                                    <textarea
                                      name="editDescription"
                                      aria-label="Edit item description"
                                      className="env-inventory-form-textarea"
                                      value={editingItemForm.description}
                                      onChange={(e) =>
                                        setEditingItemForm((prev) => ({
                                          ...prev,
                                          description: e.target.value,
                                        }))
                                      }
                                      placeholder="Item description"
                                    />
                                  ) : (
                                    <div className="env-inventory-description env-inventory-description-container">
                                      <span className="env-inventory-description-text">
                                        {item.description || "No description"}
                                      </span>
                                      {item.description && (
                                        <button
                                          className="btn env-btn-small env-inventory-eye-btn"
                                          onClick={() =>
                                            handleShowDescription(
                                              item.name,
                                              item.description
                                            )
                                          }
                                          title="View full description"
                                        >
                                          <Eye size={14} />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {item.attunement ? (
                                    item.attunedBy ? (
                                      <span className="env-attunement-status env-attunement-status-attuned">
                                        <Sparkles size={16} /> Attuned
                                      </span>
                                    ) : (
                                      <span className="env-attunement-status env-attunement-status-not-attuned">
                                        <Circle size={16} /> Not Attuned
                                      </span>
                                    )
                                  ) : (
                                    <span className="env-text-muted">n/a</span>
                                  )}
                                </td>
                                <td>
                                  <div
                                    className="env-inventory-description-container"
                                    style={{gap: 6}}
                                  >
                                    {editingItemId === item.id ? (
                                      <>
                                        <button
                                          className="btn env-btn-small"
                                          onClick={() =>
                                            saveEditInventoryItem(item.id!)
                                          }
                                          disabled={isSavingItemEdit}
                                          title="Save"
                                        >
                                          <Save size={14} />
                                        </button>
                                        <button
                                          className="btn env-btn-small"
                                          onClick={cancelEditInventoryItem}
                                          title="Cancel"
                                        >
                                          <X size={14} />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn env-btn-small"
                                          onClick={() =>
                                            startEditInventoryItem(item)
                                          }
                                          title="Edit"
                                        >
                                          <Edit size={14} />
                                        </button>
                                        {item.attunement && !item.attunedBy && (
                                          <button
                                            className="btn env-btn-small"
                                            onClick={async () => {
                                              try {
                                                await inventory.attuneToItem(
                                                  item.id!,
                                                  selectedCharacterId!
                                                );
                                                addNotification({
                                                  type: "success",
                                                  title: "Attunement Successful",
                                                  message: `Successfully attuned to ${item.name}.`,
                                                });
                                              } catch (error) {
                                                addNotification({
                                                  type: "error",
                                                  title: "Attunement Failed",
                                                  message: error instanceof Error 
                                                    ? error.message 
                                                    : "Cannot attune to more than 3 items.",
                                                });
                                              }
                                            }}
                                            title="Attune"
                                          >
                                            <Sparkles size={14} />
                                          </button>
                                        )}
                                        {item.attunedBy && (
                                          <button
                                            className="btn env-btn-small"
                                            onClick={async () => {
                                              try {
                                                await inventory.breakAttunement(
                                                  item.id!
                                                );
                                                addNotification({
                                                  type: "success",
                                                  title: "Attunement Broken",
                                                  message: `Successfully broke attunement to ${item.name}.`,
                                                });
                                              } catch (error) {
                                                addNotification({
                                                  type: "error",
                                                  title: "Failed to Break Attunement",
                                                  message: error instanceof Error 
                                                    ? error.message 
                                                    : "Failed to break attunement.",
                                                });
                                              }
                                            }}
                                            title="Break Attunement"
                                          >
                                            <Heart size={14} />
                                          </button>
                                        )}
                                        <button
                                          className="btn env-btn-small"
                                          onClick={async () => {
                                            try {
                                              await inventory.removeItem(item.id!);
                                              addNotification({
                                                type: "success",
                                                title: "Item Removed",
                                                message: `Successfully removed ${item.name} from inventory.`,
                                              });
                                            } catch (error) {
                                              addNotification({
                                                type: "error",
                                                title: "Failed to Remove Item",
                                                message: error instanceof Error 
                                                  ? error.message 
                                                  : "Failed to remove item from inventory.",
                                              });
                                            }
                                          }}
                                          title="Remove"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      {/* Add Item Form */}
                      <div className="env-inventory-form">
                        <div className="env-inventory-form-row">
                          <div className="env-inventory-form-field">
                            <div className="env-inventory-form-label">
                              Item name
                            </div>
                            <input
                              id="inventory-item-name"
                              name="name"
                              aria-label="Item name"
                              value={itemForm.name}
                              onChange={handleItemChange}
                              placeholder="Item name"
                              className="env-inventory-form-input"
                            />
                          </div>
                          <div className="env-inventory-form-field-small">
                            <div className="env-inventory-form-label">
                              Quantity
                            </div>
                            <input
                              id="inventory-item-quantity"
                              name="amount"
                              type="number"
                              aria-label="Quantity"
                              value={itemForm.amount}
                              onChange={handleItemChange}
                              placeholder="1"
                              className="env-inventory-form-input"
                            />
                          </div>
                          <div className="env-inventory-form-field-small">
                            <div className="env-inventory-form-label">
                              Attunement
                            </div>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginTop: "4px",
                              }}
                            >
                              <input
                                type="checkbox"
                                name="attunement"
                                checked={itemForm.attunement}
                                onChange={(e) =>
                                  setItemForm((prev) => ({
                                    ...prev,
                                    attunement: e.target.checked,
                                  }))
                                }
                              />
                              <span>Requires attunement</span>
                            </label>
                          </div>
                        </div>
                        <div className="env-inventory-form-field-margin">
                          <div className="env-inventory-form-label">
                            Description
                          </div>
                          <textarea
                            name="description"
                            aria-label="Item description"
                            value={itemForm.description}
                            onChange={handleItemChange}
                            placeholder="Item description"
                            className="env-inventory-form-textarea"
                          />
                        </div>
                        <button
                          className="btn btn-primary env-btn-full"
                          onClick={handleAddFirebaseItem}
                          disabled={inventory.loading}
                        >
                          <Plus
                            size={16}
                            className="env-icon-margin-right-large"
                          />
                          {inventory.loading
                            ? "Adding..."
                            : "Add Item to Inventory"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>

            {/* Downtime Spent Section */}
            <div className="env-downtime-section">
              <div className="env-downtime-header">DOWNTIME SPENT</div>
              <div className="env-downtime-content">
                {characterActivity ? (
                  <table className="env-downtime-table">
                    <thead>
                      <tr>
                        <th>Activity</th>
                        <th style={{width: "120px", textAlign: "right"}}>
                          Total Days
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(
                        (characterActivity.activities || [])
                          .filter((a) => a.type === "downtime_spent")
                          .reduce((acc, curr) => {
                            const desc = curr.description;
                            const days = curr.downtimeDaysSpent || 0;
                            acc[desc] = (acc[desc] || 0) + days;
                            return acc;
                          }, {} as Record<string, number>)
                      ).length === 0 ? (
                        <tr>
                          <td colSpan={2} className="env-downtime-empty">
                            No downtime spent yet
                          </td>
                        </tr>
                      ) : (
                        Object.entries(
                          (characterActivity.activities || [])
                            .filter((a) => a.type === "downtime_spent")
                            .reduce((acc, curr) => {
                              const desc = curr.description;
                              const days = curr.downtimeDaysSpent || 0;
                              acc[desc] = (acc[desc] || 0) + days;
                              return acc;
                            }, {} as Record<string, number>)
                        )
                          .sort((a, b) => b[1] - a[1]) // Sort by days descending
                          .map(([description, totalDays]) => (
                            <tr key={description}>
                              <td className="env-downtime-name">
                                {description}
                              </td>
                              <td
                                className="env-downtime-amount"
                                style={{textAlign: "right"}}
                              >
                                {totalDays}
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div className="env-loading">Loading activity...</div>
                )}
              </div>
            </div>

            {/* Character Sheet Section */}
            <CharacterSheetUpload
              characterId={characters[viewIndex].id || ""}
              currentSheetUrl={characters[viewIndex].sheetUrl}
              sheetPath={characters[viewIndex].sheetPath}
              fileName={characters[viewIndex].fileName}
              onSheetUploaded={(sheetUrl) => {
                // Update the character data in the local state
                const updatedCharacters = [...characters];
                updatedCharacters[viewIndex] = {
                  ...updatedCharacters[viewIndex],
                  sheetUrl,
                };
                // Note: The real-time listener will update this automatically
              }}
              onSheetDeleted={() => {
                // Update the character data in the local state
                const updatedCharacters = [...characters];
                updatedCharacters[viewIndex] = {
                  ...updatedCharacters[viewIndex],
                  sheetUrl: undefined,
                  sheetPath: undefined,
                  fileName: undefined,
                };
                // Note: The real-time listener will update this automatically
              }}
            />

            {/* Will Section */}
            <div className="env-will-section">
              <div className="env-will-header">WILL</div>
              <div className="env-form-group">
                <label htmlFor="will-beneficiary">
                  Beneficiary (e.g., Local Thieves Guild, Family Estate)
                </label>
                <input
                  id="will-beneficiary"
                  name="will.beneficiary"
                  placeholder="Beneficiary (e.g., Local Thieves Guild, Family Estate)"
                  value={editForm.will?.beneficiary || ""}
                  onChange={handleEditFormChange}
                  className="env-input"
                />
              </div>
              <div className="env-form-group">
                <label htmlFor="will-description">
                  Description of what should happen to your character's
                  possessions and memento tokens
                </label>
                <textarea
                  id="will-description"
                  name="will.description"
                  placeholder="Description of what should happen to your character's possessions and memento tokens"
                  value={editForm.will?.description || ""}
                  onChange={handleEditFormChange}
                  className="env-textarea"
                  rows={4}
                />
              </div>
              <div className="env-will-description">
                Your will determines what happens to your character's
                possessions and memento tokens if they meet an untimely end.
              </div>
            </div>
            {/* Save Button */}
            <button
              className="btn btn-primary env-btn-full"
              onClick={handleSave}
              disabled={isSubmitting}
            >
              <Save size={16} className="env-icon-margin-right-large" />
              {isSubmitting ? "Saving..." : "Save"}
            </button>

            {/* Delete Character Button */}
            <button
              className="btn btn-destructive env-btn-full env-delete-character-margin"
              onClick={handleDeleteCharacter}
            >
              <Trash2 size={16} className="env-icon-margin-right-large" />
              Delete Character
            </button>
          </div>
        )}
      </section>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        message="Are you sure you want to remove this item from your inventory?"
      />

      {/* Level Up Confirmation Dialog */}
      <Dialog
        open={levelUpDialogOpen && viewIndex !== null}
        onClose={handleCancelLevelUp}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Level Up Confirmation
          <IconButton
            aria-label="close"
            onClick={handleCancelLevelUp}
            disabled={isLevelingUp}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewIndex !== null && (
            <>
              <Typography variant="body1" sx={{mb: 2}}>
                Are you sure you want to level up{" "}
                <strong>{characters[viewIndex].name}</strong>?
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Box
                  sx={{display: "flex", justifyContent: "space-between", mb: 1}}
                >
                  <Typography variant="body2" color="text.secondary">
                    Current Level:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {ensureNumber(characters[viewIndex].level)}
                  </Typography>
                </Box>

                <Box
                  sx={{display: "flex", justifyContent: "space-between", mb: 1}}
                >
                  <Typography variant="body2" color="text.secondary">
                    New Level:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {ensureNumber(characters[viewIndex].level) + 1}
                  </Typography>
                </Box>

                <Box
                  sx={{display: "flex", justifyContent: "space-between", mb: 1}}
                >
                  <Typography variant="body2" color="text.secondary">
                    Tokens Required:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {getTokensRequiredForNextLevel(
                      ensureNumber(characters[viewIndex].level)
                    )}
                  </Typography>
                </Box>

                <Box sx={{display: "flex", justifyContent: "space-between"}}>
                  <Typography variant="body2" color="text.secondary">
                    Tokens After Level Up:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {ensureNumber(characters[viewIndex].mementoTokens) -
                      getTokensRequiredForNextLevel(
                        ensureNumber(characters[viewIndex].level)
                      )}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <button
            className="btn btn-secondary"
            onClick={handleCancelLevelUp}
            disabled={isLevelingUp}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirmLevelUp}
            disabled={isLevelingUp}
          >
            {isLevelingUp ? "Leveling Up..." : "Level Up"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Downtime Spending Confirmation Dialog */}
      <Dialog
        open={downtimeDialogOpen && viewIndex !== null}
        onClose={handleCancelSpendDowntime}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Spend Downtime Days
          <IconButton
            aria-label="close"
            onClick={handleCancelSpendDowntime}
            disabled={isSpendingDowntime}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewIndex !== null && (
            <>
              <Typography variant="body1" sx={{mb: 2}}>
                How many downtime days do you want to spend for{" "}
                <strong>{characters[viewIndex].name}</strong>?
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.200",
                  mb: 2,
                }}
              >
                <Box
                  sx={{display: "flex", justifyContent: "space-between", mb: 1}}
                >
                  <Typography variant="body2" color="text.secondary">
                    Available Downtime Days:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {ensureNumber(characters[viewIndex].downtimeDays)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{mb: 2}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                  Days to Spend:
                </Typography>
                <input
                  type="number"
                  max={ensureNumber(characters[viewIndex].downtimeDays)}
                  value={downtimeDaysToSpend}
                  onChange={(e) =>
                    setDowntimeDaysToSpend(
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="env-input env-dialog-input-full"
                  disabled={isSpendingDowntime}
                />
              </Box>

              <Box sx={{mb: 3}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                  Activity Description:
                </Typography>
                <Autocomplete
                  freeSolo
                  options={Array.from(
                    new Set(
                      (characterActivity?.activities || [])
                        .filter((a) => a.type === "downtime_spent")
                        .map((a) => a.description)
                    )
                  )}
                  value={downtimeDescription}
                  onInputChange={(_, newValue) => {
                    setDowntimeDescription(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="e.g. Relaxation, Trading, Carousing..."
                      variant="outlined"
                      size="small"
                      disabled={isSpendingDowntime}
                      fullWidth
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "#fff3cd",
                  borderRadius: 1,
                  border: "1px solid #ffeaa7",
                }}
              >
                <Typography
                  variant="body2"
                  color="#856404"
                  sx={{fontWeight: 500}}
                >
                  ⚠️ Warning: This action cannot be undone. Downtime days can
                  only be gained from session logs.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <button
            className="btn btn-secondary"
            onClick={handleCancelSpendDowntime}
            disabled={isSpendingDowntime}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary env-dialog-btn-downtime"
            onClick={handleConfirmSpendDowntime}
            disabled={
              isSpendingDowntime ||
              downtimeDaysToSpend <= 0 ||
              downtimeDaysToSpend >
                ensureNumber(characters[viewIndex!]?.downtimeDays || 0)
            }
          >
            {isSpendingDowntime
              ? "Spending..."
              : downtimeDaysToSpend > 0
              ? `Spend ${downtimeDaysToSpend} Days`
              : "Spend Days"}
          </button>
        </DialogActions>
      </Dialog>

      {/* DM Token Transfer Dialog */}
      <Dialog
        open={dmTokenDialogOpen && viewIndex !== null}
        onClose={handleCancelTransferDMTokens}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Transfer DM Tokens
          <IconButton
            aria-label="close"
            onClick={handleCancelTransferDMTokens}
            disabled={isTransferringTokens}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewIndex !== null && (
            <>
              <Typography variant="body1" sx={{mb: 2}}>
                How many DM tokens do you want to transfer to{" "}
                <strong>{characters[viewIndex].name}</strong>?
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.200",
                  mb: 2,
                }}
              >
                <Box
                  sx={{display: "flex", justifyContent: "space-between", mb: 1}}
                >
                  <Typography variant="body2" color="text.secondary">
                    Your Available DM Tokens:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {getUserDMTokens()}
                  </Typography>
                </Box>
                <Box sx={{display: "flex", justifyContent: "space-between"}}>
                  <Typography variant="body2" color="text.secondary">
                    Character's Current Memento Tokens:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {ensureNumber(characters[viewIndex].mementoTokens)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{mb: 2}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                  Tokens to Transfer:
                </Typography>
                <input
                  type="number"
                  min="1"
                  max={getUserDMTokens()}
                  value={tokensToTransfer}
                  onChange={(e) =>
                    setTokensToTransfer(
                      Math.max(
                        1,
                        Math.min(
                          getUserDMTokens(),
                          parseInt(e.target.value) || 1
                        )
                      )
                    )
                  }
                  className="env-input env-dialog-input-full"
                  disabled={isTransferringTokens}
                />
              </Box>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "#e3f2fd",
                  borderRadius: 1,
                  border: "1px solid #bbdefb",
                }}
              >
                <Typography
                  variant="body2"
                  color="#0d47a1"
                  sx={{fontWeight: 500}}
                >
                  ℹ️ Info: DM tokens are earned by running sessions and can be
                  transferred to any character to help them level up.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <button
            className="btn btn-secondary"
            onClick={handleCancelTransferDMTokens}
            disabled={isTransferringTokens}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary env-dialog-btn-dm"
            onClick={handleConfirmTransferDMTokens}
            disabled={
              isTransferringTokens ||
              tokensToTransfer <= 0 ||
              tokensToTransfer > getUserDMTokens()
            }
          >
            {isTransferringTokens
              ? "Transferring..."
              : `Transfer ${tokensToTransfer} Token${tokensToTransfer !== 1 ? "s" : ""}`}
          </button>
        </DialogActions>
      </Dialog>

      {/* Description Modal */}
      <Dialog
        open={descriptionModalOpen && selectedDescription !== null}
        onClose={handleCloseDescription}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedDescription?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseDescription}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{lineHeight: 1.6, color: "#333"}}>
            {selectedDescription?.description}
          </Typography>
        </DialogContent>
        <DialogActions sx={{p: 2}}>
          <button className="btn btn-primary" onClick={handleCloseDescription}>
            Close
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete Character Confirmation Dialog */}
      <Dialog
        open={deleteCharacterDialogOpen && viewIndex !== null}
        onClose={handleCancelDeleteCharacter}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Delete Character
          <IconButton
            aria-label="close"
            onClick={handleCancelDeleteCharacter}
            disabled={isDeletingCharacter}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewIndex !== null && (
            <>
              <Typography variant="body1" sx={{mb: 2}}>
                Are you sure you want to delete{" "}
                <strong>{characters[viewIndex].name}</strong>? This action is
                permanent and cannot be undone.
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f8d7da",
                  borderRadius: 1,
                  border: "1px solid #f5c6cb",
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="#721c24"
                  sx={{fontWeight: 500}}
                >
                  ⚠️ Warning: This will permanently delete your character and
                  all associated data including:
                </Typography>
                <ul className="env-dialog-warning-list">
                  <li>Character sheet and files</li>
                  <li>Inventory and currency</li>
                  <li>Character metadata and logs</li>
                  <li>All character progress and history</li>
                </ul>
              </Box>

              <Box sx={{mb: 2}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                  To confirm deletion, please type the character name exactly as
                  it appears:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{mb: 1, color: "#dc3545"}}
                >
                  "{characters[viewIndex].name}"
                </Typography>
                <input
                  type="text"
                  value={deleteCharacterName}
                  onChange={(e) => setDeleteCharacterName(e.target.value)}
                  placeholder="Type character name to confirm"
                  className="env-input env-dialog-input-confirm"
                  disabled={isDeletingCharacter}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{p: 2, gap: 1}}>
          <button
            className="btn btn-secondary"
            onClick={handleCancelDeleteCharacter}
            disabled={isDeletingCharacter}
          >
            Cancel
          </button>
          <button
            className="btn btn-destructive"
            onClick={handleConfirmDeleteCharacter}
            disabled={
              isDeletingCharacter ||
              deleteCharacterName.trim() !==
                (characters[viewIndex!]?.name || "").trim()
            }
          >
            {isDeletingCharacter ? "Deleting..." : "Delete Character"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Characters;
